import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronRight, ArrowUpRight, Mail } from "lucide-react";
import { NAV_LINKS, CONTACT } from "../lib/site";
import Logo from "./Logo";
import { track } from "../lib/analytics";
export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const menuToggle = useRef(null);
  useEffect(() => {
    if (!open) return;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") { setOpen(false); menuToggle.current?.focus(); }
    };
    const closeOnDesktop = () => { if (window.innerWidth >= 1024) setOpen(false); };
    window.addEventListener("keydown", closeOnEscape);
    window.addEventListener("resize", closeOnDesktop);
    return () => { window.removeEventListener("keydown", closeOnEscape); window.removeEventListener("resize", closeOnDesktop); };
  }, [open]);
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);
  // Broadcast mobile-menu state to the rest of the app (e.g. StickyInquiryRail)
  // via a body class, and lock body scroll while the drawer is open.
  useEffect(() => {
    const cls = "mobile-menu-open";
    if (open) {
      document.body.classList.add(cls);
    } else {
      document.body.classList.remove(cls);
    }
    return () => document.body.classList.remove(cls);
  }, [open]);
  useEffect(() => {
    let ticking = false;
    const update = () => {
      setScrolled(window.scrollY > 16);
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 max-h-[100dvh] flex flex-col bg-white border-b transition-shadow duration-500 ${
        scrolled ? "border-line shadow-[0_2px_12px_rgba(11,28,56,0.06)]" : "border-transparent"
      }`}
      data-testid="site-header"
    >
      {/* Engineering availability strip — credentials left, single booking CTA right */}
      <div
        className="hidden lg:flex items-center justify-between bg-navy text-white text-[15.5px] font-mono tracking-[0.28em] uppercase px-8 py-2"
        data-testid="engineering-strip"
      >
        <div className="flex items-center gap-7">
          <span className="text-white/80">Grand Junction, CO</span>
        </div>
        <Link
          to="/contact-us"
          className="inline-flex items-center gap-2 text-white hover:text-[#ffb4b8] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white transition-colors"
          data-testid="engineering-strip-book"
        >
          <Mail className="w-3 h-3" strokeWidth={2} />
          Start a Conversation
          <ArrowUpRight className="w-3 h-3" strokeWidth={2} />
        </Link>
      </div>
      {/* Main bar — big, bold, defense-major */}
      <div className="flex items-center justify-between h-20 md:h-24 xl:h-28 w-full shrink-0">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center gap-3 group shrink-0 pl-4 md:pl-10"
          data-testid="logo-link"
        >
          <Logo size="lg" variant="light" showWordmark={false} />
        </Link>
        {/* Center Nav — bold, sentence case, big */}
        <nav
          className="hidden lg:flex items-center justify-center gap-2.5 xl:gap-6 2xl:gap-10 flex-1 min-w-0"
          data-testid="desktop-nav"
        >
          {NAV_LINKS.slice(1, -1).map((l) =>
            l.external ? (
              <a
                key={l.to}
                href={l.to}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`nav-${l.label.toLowerCase()}`}
                className="relative text-[12.5px] xl:text-[16px] font-display font-semibold tracking-tight whitespace-nowrap text-navy/90 hover:text-navy transition-colors py-2 group inline-flex items-center gap-1.5"
              >
                {l.label}
                <ArrowUpRight className="w-3.5 h-3.5 opacity-60 group-hover:opacity-100 transition-opacity" />
                <span className="absolute -left-2 -right-2 -bottom-0.5 h-[2.5px] bg-signal origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
              </a>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) =>
                  `relative text-[12.5px] xl:text-[16px] font-display font-semibold tracking-tight whitespace-nowrap transition-colors py-2 group ${
                    isActive ? "text-navy" : "text-navy/90 hover:text-navy"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    <span
                      className={`absolute -left-2 -right-2 -bottom-0.5 h-[2.5px] bg-signal origin-left transition-transform duration-500 ${
                        isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            )
          )}
        </nav>
        {/* Right — Submit Inquiry CTA + mobile burger */}
        <div className="flex items-center gap-2 md:gap-3 pr-4 md:pr-10 shrink-0">
          <Link
            to="/contact-us"
            data-testid="header-cta-contact"
            className="hidden sm:inline-flex group items-center gap-2 lg:gap-3 bg-signal text-white px-4 lg:px-5 xl:px-8 py-3 lg:py-3 xl:py-4 text-[12px] lg:text-[12.5px] xl:text-[14px] font-display font-semibold tracking-tight uppercase hover:bg-navy transition-all duration-500 hover:gap-4"
          >
            Submit Inquiry
            <span className="inline-flex w-5 h-5 items-center justify-center bg-white/15 group-hover:bg-white/20 transition-colors">
              <ChevronRight className="w-3.5 h-3.5" />
            </span>
          </Link>
          {/* Mobile / tablet burger */}
          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-11 h-11 border border-navy/30 text-navy hover:bg-navy hover:text-white transition-colors flex items-center justify-center"
            ref={menuToggle}
            aria-expanded={open}
            aria-controls="mobile-site-navigation"
            aria-label="Toggle navigation"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {/* Mobile / tablet menu */}
      {open && (
        <div
          id="mobile-site-navigation"
          className="lg:hidden min-h-0 overflow-y-auto overscroll-contain bg-white border-t border-line"
          data-testid="mobile-menu"
        >
          <nav className="px-6 py-6 flex flex-col gap-1">
            {NAV_LINKS.slice(1).map((l) =>
              l.external ? (
                <a
                  key={l.to}
                  href={l.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  className="flex items-center justify-between py-4 border-b border-line/70 font-display tracking-tight text-lg font-semibold text-navy/90"
                >
                  <span>{l.label}</span>
                  <ArrowUpRight className="w-4 h-4" />
                </a>
              ) : (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => {
                    // Same-page tap: just close the menu — DO NOT scroll.
                    // Different page: React Router navigates and Layout's
                    // useLayoutEffect handles the scroll reset to top.
                    setOpen(false);
                  }}
                  data-testid={`mobile-nav-${l.to.replace(/\//g, "") || "home"}`}
                  className={({ isActive }) =>
                    `flex items-center justify-between py-4 border-b border-line/70 font-display tracking-tight text-lg font-semibold ${
                      isActive ? "text-navy" : "text-navy/90"
                    }`
                  }
                >
                  <span>{l.label}</span>
                  <ArrowUpRight className="w-4 h-4" />
                </NavLink>
              )
            )}
            <Link
              to="/contact-us"
              onClick={() => {
                setOpen(false);
              }}
              className="mt-6 inline-flex items-center justify-center gap-2 bg-signal text-white px-6 py-4 font-display font-semibold tracking-tight text-base hover:bg-navy transition-colors"
              data-testid="mobile-cta-contact"
            >
              Submit Inquiry
              <ChevronRight className="w-4 h-4" />
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}